import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  setActiveCustomerDisplay,
  setEnabledCustomerDisplay,
} from 'redux/actions/customerDisplayActions'
import { Formatter } from 'Utils/Formatter'
import { ROUTES } from 'Utils/constants'
import {
  isCustomerDisplayRouteSupported,
  shouldResetCustomerDisplayView,
} from 'Views/CustomerDisplay/utils/customerDisplayRouteSupport'
import {
  CUSTOMER_DISPLAY_OWNER_TAB_ID_STORAGE_KEY,
  CUSTOMER_DISPLAY_OWNER_TAB_STATUS_STORAGE_KEY,
  clearCustomerDisplayOwnerTabId,
  createCustomerDisplayChannel,
  getCustomerDisplayOwnerTabId,
  getCustomerDisplayOwnerTabStatus,
  getCustomerDisplayTabId,
  isCustomerDisplayMessageForCurrentTab,
  isCustomerDisplaySourceMessage,
  notifyCustomerDisplayMainTabClosing,
  postCustomerDisplayMainMessage,
  setCustomerDisplayOwnerTabId,
  setCustomerDisplayOwnerTabStatus,
} from 'Views/CustomerDisplay/utils/customerDisplayChannel'
import {
  closeCustomerDisplayWindowHandle,
  hasCustomerDisplaySecondaryScreen,
  isCustomerDisplayWindowHandle,
  navigateCustomerDisplayWindow,
  openCustomerDisplayWindow,
  prepareCustomerDisplayWindowConfig,
  watchSecondaryScreenDisconnect,
} from 'Views/CustomerDisplay/utils/customerDisplayWindow'
import { useGracePeriodTimeout } from 'Views/CustomerDisplay/hook/useGracePeriodTimeout'
import {
  acquireCustomerDisplayMainAppPresence,
  hasActiveCustomerDisplayWindowPresence,
} from 'Views/CustomerDisplay/utils/customerDisplayPresence'
import { createCustomerDisplayId } from 'Views/CustomerDisplay/utils/customerDisplayId'

const getCartTotals = cart => {
  let totalTax = 0
  let totalPrice = 0

  cart.forEach(item => {
    const price = Number(item?.Price) || 0
    const quantity = Number(item?.Quantity) || 0
    const additionalDiscount = Number(item?.VisitDiscountAmount) || 0
    const taxRate = Number(item?.TaxCategoryValue) || 0
    const subtotalLine = Number(
      Formatter.Money(price * quantity - additionalDiscount, {
        withFormat: false,
        rounded: true,
        decimalPrecision: 2,
      })
    )

    totalTax += subtotalLine * taxRate
    totalPrice += subtotalLine
  })

  const subtotal = Number(
    Formatter.Money(totalPrice, {
      decimalPrecision: 2,
      withFormat: false,
    })
  )
  const itbms = Number(
    Formatter.Money(totalTax, {
      withFormat: false,
    })
  )

  return {
    subtotal,
    itbms,
    total: Number(subtotal) + Number(totalTax),
  }
}

const CUSTOMER_DISPLAY_HEARTBEAT_INTERVAL_MS = 2000
const CUSTOMER_DISPLAY_HEARTBEAT_ACK_TIMEOUT_MS = 6000
const CUSTOMER_DISPLAY_HEARTBEAT_ACK_CHECK_INTERVAL_MS = 1000
const CUSTOMER_DISPLAY_WINDOW_CLOSE_GRACE_PERIOD_MS = 15000
const CUSTOMER_DISPLAY_OWNER_STALE_MS = 10000
const CUSTOMER_DISPLAY_OWNERSHIP_CLAIM_DEBOUNCE_MS = 500
const CUSTOMER_DISPLAY_OWNERSHIP_HANDOFF_RETRY_MS = 2000
const CUSTOMER_DISPLAY_CLOSING_FALLBACK_TIMEOUT_MS = 5000

let lastMainAppPathname = null

const createOwnershipRequestId = createCustomerDisplayId

const isMainAppTabVisible = () =>
  typeof document === 'undefined' || !document.hidden

const isMainAppTabFocused = () =>
  isMainAppTabVisible() &&
  (typeof document.hasFocus !== 'function' || document.hasFocus())

const hasConfirmedCustomerDisplayConnection = (ownerStatus, ownerTabId) =>
  Boolean(
    ownerTabId &&
      ownerStatus?.tabId === ownerTabId &&
      ownerStatus.isConnected &&
      ownerStatus.updatedAt &&
      Date.now() - ownerStatus.updatedAt <= CUSTOMER_DISPLAY_OWNER_STALE_MS
  )

const hasConfirmedCustomerDisplayConnectionForTab = (
  ownerStatus,
  ownerTabId,
  tabId
) =>
  ownerTabId === tabId &&
  hasConfirmedCustomerDisplayConnection(ownerStatus, ownerTabId)

const isEnteringCreateQuote = (prevPathname, nextPathname) =>
  nextPathname.includes(ROUTES.createQuote) &&
  shouldResetCustomerDisplayView(prevPathname, nextPathname)

const createCustomerDisplayProductViewState = () => ({
  isSearchView: false,
  isLoading: false,
  listProducts: [],
  isProductDetailOpen: false,
  productDetail: null,
  pendingSearchNavigation: false,
})

export function useCustomerDisplayController() {
  const dispatch = useDispatch()
  const location = useLocation()
  const customerDisplayTabIdRef = useRef(getCustomerDisplayTabId())
  const shouldInitializeCreateQuoteInIdleRef = useRef(
    location.pathname.includes(ROUTES.createQuote)
  )
  const preserveIdleOnCreateQuoteEntryRef = useRef(
    shouldInitializeCreateQuoteInIdleRef.current ||
      isEnteringCreateQuote(
        lastMainAppPathname || location.pathname,
        location.pathname
      )
  )
  const [loaderFullScreen, setLoaderFullScreen] = useState(false)
  const [customerDisplayActivationError, setCustomerDisplayActivationError] =
    useState('')
  const [
    isCustomerDisplayWindowConnected,
    setIsCustomerDisplayWindowConnected,
  ] = useState(() => {
    const ownerTabId = getCustomerDisplayOwnerTabId()
    return hasConfirmedCustomerDisplayConnectionForTab(
      getCustomerDisplayOwnerTabStatus(),
      ownerTabId,
      customerDisplayTabIdRef.current
    )
  })
  const [isCustomerDisplaySwitchActive, setIsCustomerDisplaySwitchActive] =
    useState(() => {
      const ownerTabId = getCustomerDisplayOwnerTabId()
      return hasConfirmedCustomerDisplayConnectionForTab(
        getCustomerDisplayOwnerTabStatus(),
        ownerTabId,
        customerDisplayTabIdRef.current
      )
    })
  const [
    isCurrentTabCustomerDisplayOwner,
    setIsCurrentTabCustomerDisplayOwner,
  ] = useState(
    () => getCustomerDisplayOwnerTabId() === customerDisplayTabIdRef.current
  )
  const channelRef = useRef(null)
  const activeCustomerDisplayWindowRef = useRef(null)
  const lastCustomerDisplayHeartbeatAckRef = useRef(null)
  const customerDisplayHeartbeatMonitorStartedAtRef = useRef(null)
  const lastOwnershipClaimAtRef = useRef(0)
  const pendingOwnershipRequestRef = useRef(null)
  const claimOwnershipIfInteractiveRef = useRef(null)
  const isCustomerDisplayWindowConnectedRef = useRef(
    isCustomerDisplayWindowConnected
  )
  const isCustomerDisplayOwnershipConfirmedRef = useRef(
    isCustomerDisplayWindowConnected
  )
  const mainAppTabCloseNotifiedRef = useRef(false)
  const locationPathnameRef = useRef(location.pathname)
  const isMainAppTabHiddenRef = useRef(
    typeof document !== 'undefined' ? document.hidden : false
  )
  const {
    clearGracePeriodTimeout: clearCustomerDisplayCloseTimeout,
    scheduleGracePeriodTimeout: scheduleCustomerDisplayCloseTimeout,
  } = useGracePeriodTimeout()
  const {
    clearGracePeriodTimeout: clearOwnershipHandoffTimeout,
    scheduleGracePeriodTimeout: scheduleOwnershipHandoffTimeout,
  } = useGracePeriodTimeout()
  const {
    clearGracePeriodTimeout: clearOwnerClosingFallbackTimeout,
    scheduleGracePeriodTimeout: scheduleOwnerClosingFallbackTimeout,
  } = useGracePeriodTimeout()

  const { isActiveCustomerDisplay = false, isCustomerDisplayEnabled } =
    useSelector(state => state.customerDisplay) || {}
  const customerDisplayEnabled =
    isCustomerDisplayEnabled ?? isActiveCustomerDisplay
  const shouldMonitorCustomerDisplayConnection =
    isCurrentTabCustomerDisplayOwner &&
    customerDisplayEnabled &&
    (isActiveCustomerDisplay || isCustomerDisplayWindowConnected)
  const customerDisplayEnabledRef = useRef(customerDisplayEnabled)
  const isActiveCustomerDisplayRef = useRef(isActiveCustomerDisplay)
  const { CompanyId, BranchOfficeId } =
    useSelector(state => state.company) || {}
  const cart = useSelector(state => state.cart?.cart || [])
  const quoteHeader = useSelector(state => state.cotizacion?.QuoteHeader || {})

  const companyStateRef = useRef({ CompanyId, BranchOfficeId })
  const customerDisplayStateRef = useRef({ cart, quoteHeader })
  const customerDisplayProductViewStateRef = useRef(
    createCustomerDisplayProductViewState()
  )

  useEffect(() => {
    companyStateRef.current = { CompanyId, BranchOfficeId }
  }, [CompanyId, BranchOfficeId])

  useEffect(() => {
    const releasePresence = acquireCustomerDisplayMainAppPresence(
      customerDisplayTabIdRef.current
    )

    return () => {
      releasePresence?.()
    }
  }, [])

  useEffect(() => {
    customerDisplayStateRef.current = { cart, quoteHeader }
  }, [cart, quoteHeader])

  useEffect(() => {
    customerDisplayEnabledRef.current = customerDisplayEnabled
  }, [customerDisplayEnabled])

  useEffect(() => {
    isActiveCustomerDisplayRef.current = isActiveCustomerDisplay
  }, [isActiveCustomerDisplay])

  useEffect(() => {
    isCustomerDisplayWindowConnectedRef.current =
      isCustomerDisplayWindowConnected
  }, [isCustomerDisplayWindowConnected])

  const getChannel = useCallback(() => {
    if (!channelRef.current && typeof BroadcastChannel !== 'undefined') {
      channelRef.current = createCustomerDisplayChannel()
    }

    return channelRef.current
  }, [])

  const postMessageToCustomerDisplay = useCallback(
    message => {
      postCustomerDisplayMainMessage(getChannel(), message)
    },
    [getChannel]
  )

  useEffect(() => {
    const prevPathname = lastMainAppPathname || locationPathnameRef.current
    locationPathnameRef.current = location.pathname
    lastMainAppPathname = location.pathname

    const shouldInitializeInIdle = shouldInitializeCreateQuoteInIdleRef.current
    shouldInitializeCreateQuoteInIdleRef.current = false

    if (
      !shouldInitializeInIdle &&
      !shouldResetCustomerDisplayView(prevPathname, location.pathname)
    ) {
      return
    }

    preserveIdleOnCreateQuoteEntryRef.current =
      shouldInitializeInIdle ||
      isEnteringCreateQuote(prevPathname, location.pathname)
    customerDisplayProductViewStateRef.current =
      createCustomerDisplayProductViewState()

    if (
      isCustomerDisplayRouteSupported(location.pathname) &&
      customerDisplayEnabledRef.current &&
      getCustomerDisplayOwnerTabId() === customerDisplayTabIdRef.current
    ) {
      postMessageToCustomerDisplay({ forceIdleMode: true })
    }
  }, [location.pathname, postMessageToCustomerDisplay])

  const markCustomerDisplayAsClosed = useCallback(() => {
    clearCustomerDisplayCloseTimeout()
    clearOwnershipHandoffTimeout()
    pendingOwnershipRequestRef.current = null
    activeCustomerDisplayWindowRef.current = null
    lastCustomerDisplayHeartbeatAckRef.current = null
    customerDisplayHeartbeatMonitorStartedAtRef.current = null
    customerDisplayEnabledRef.current = false
    isActiveCustomerDisplayRef.current = false
    isCustomerDisplayWindowConnectedRef.current = false
    isCustomerDisplayOwnershipConfirmedRef.current = false
    clearCustomerDisplayOwnerTabId(customerDisplayTabIdRef.current)
    setIsCurrentTabCustomerDisplayOwner(false)
    setIsCustomerDisplayWindowConnected(false)
    setIsCustomerDisplaySwitchActive(false)
    setLoaderFullScreen(false)
    dispatch(setEnabledCustomerDisplay(false))
    dispatch(setActiveCustomerDisplay(false))
  }, [clearCustomerDisplayCloseTimeout, clearOwnershipHandoffTimeout, dispatch])

  const closeCustomerDisplayWindow = useCallback(() => {
    const customerDisplayWindow = activeCustomerDisplayWindowRef.current

    closeCustomerDisplayWindowHandle(customerDisplayWindow)

    activeCustomerDisplayWindowRef.current = null
  }, [])

  const markCustomerDisplayAsDisconnected = useCallback(
    ({ preserveSwitchState = false } = {}) => {
      clearCustomerDisplayCloseTimeout()
      activeCustomerDisplayWindowRef.current = null
      lastCustomerDisplayHeartbeatAckRef.current = null
      customerDisplayHeartbeatMonitorStartedAtRef.current = null
      isActiveCustomerDisplayRef.current = false
      isCustomerDisplayWindowConnectedRef.current = false
      isCustomerDisplayOwnershipConfirmedRef.current = false
      setIsCustomerDisplayWindowConnected(false)

      const ownerTabId = customerDisplayTabIdRef.current
      if (getCustomerDisplayOwnerTabId() === ownerTabId) {
        setCustomerDisplayOwnerTabStatus({
          tabId: ownerTabId,
          isHidden: isMainAppTabHiddenRef.current,
          isConnected: false,
        })
      }

      if (!preserveSwitchState) {
        setIsCustomerDisplaySwitchActive(false)
      }

      setLoaderFullScreen(false)
      dispatch(setActiveCustomerDisplay(false))
    },
    [clearCustomerDisplayCloseTimeout, dispatch]
  )

  const sendCompanyData = useCallback(() => {
    postMessageToCustomerDisplay({
      company: companyStateRef.current.CompanyId,
      branchOffice: companyStateRef.current.BranchOfficeId,
      isChangeCompany: false,
    })
  }, [postMessageToCustomerDisplay])

  const notifyMainRouteSupport = useCallback(() => {
    postMessageToCustomerDisplay({
      mainRouteIsSupported: isCustomerDisplayRouteSupported(
        locationPathnameRef.current
      ),
    })
  }, [postMessageToCustomerDisplay])

  const refreshCustomerDisplayOwnerStatus = useCallback(
    ({ isHidden = isMainAppTabHiddenRef.current, isClosing = false } = {}) => {
      const ownerTabId = customerDisplayTabIdRef.current

      if (getCustomerDisplayOwnerTabId() !== ownerTabId) return

      setCustomerDisplayOwnerTabStatus({
        tabId: ownerTabId,
        isHidden,
        isClosing,
        isConnected: isCustomerDisplayWindowConnectedRef.current,
      })
    },
    []
  )

  const sendCustomerDisplayHeartbeat = useCallback(() => {
    refreshCustomerDisplayOwnerStatus()
    postMessageToCustomerDisplay({
      customerDisplayHeartbeat: Date.now(),
    })
  }, [postMessageToCustomerDisplay, refreshCustomerDisplayOwnerStatus])

  const confirmCustomerDisplayConnection = useCallback(() => {
    if (getCustomerDisplayOwnerTabId() !== customerDisplayTabIdRef.current) {
      return false
    }

    const now = Date.now()
    lastCustomerDisplayHeartbeatAckRef.current = now
    customerDisplayHeartbeatMonitorStartedAtRef.current = now
    isCustomerDisplayWindowConnectedRef.current = true
    isCustomerDisplayOwnershipConfirmedRef.current = true
    setIsCustomerDisplayWindowConnected(true)
    setIsCustomerDisplaySwitchActive(true)
    setIsCurrentTabCustomerDisplayOwner(true)
    refreshCustomerDisplayOwnerStatus()

    return true
  }, [refreshCustomerDisplayOwnerStatus])

  const cacheCustomerDisplayProductViewState = useCallback(data => {
    if (data.tabId !== customerDisplayTabIdRef.current) return

    if (data.isDetailQuote) {
      customerDisplayProductViewStateRef.current =
        createCustomerDisplayProductViewState()
      return
    }

    const currentView = customerDisplayProductViewStateRef.current
    const nextView = { ...currentView }

    if (data.isSearchProducts === true) {
      nextView.isSearchView = true
      nextView.pendingSearchNavigation =
        !isCustomerDisplayOwnershipConfirmedRef.current ||
        getCustomerDisplayOwnerTabId() !== customerDisplayTabIdRef.current
    }

    if (Object.prototype.hasOwnProperty.call(data, 'listProducts')) {
      nextView.listProducts = Array.isArray(data.listProducts)
        ? data.listProducts
        : []
    }

    if (
      Object.prototype.hasOwnProperty.call(data, 'isLoading') &&
      (data.isSearchProducts === true || data.listProducts !== undefined)
    ) {
      nextView.isLoading = Boolean(data.isLoading)
    }

    if (data.isProductDetail === true) {
      nextView.isProductDetailOpen = true
      nextView.productDetail = data.productDetail || currentView.productDetail
    } else if (data.isProductDetail === false) {
      nextView.isProductDetailOpen = false
      nextView.productDetail = null
    }

    customerDisplayProductViewStateRef.current = nextView
  }, [])

  const syncCustomerDisplayState = useCallback(
    ({ forceNavigation } = {}) => {
      const pathname = locationPathnameRef.current
      if (!isCustomerDisplayRouteSupported(pathname)) return

      const { cart: currentCart, quoteHeader: currentQuoteHeader } =
        customerDisplayStateRef.current
      const cartTotals = getCartTotals(currentCart)
      const shouldShowQuoteDetail =
        pathname.includes(ROUTES.editQuoteHeaderId) &&
        !pathname.includes('/edit_cart')
      const shouldPreserveIdle =
        forceNavigation && preserveIdleOnCreateQuoteEntryRef.current

      postMessageToCustomerDisplay({
        productCart: currentCart,
        subtotal: cartTotals.subtotal,
        itbms: cartTotals.itbms,
        total: cartTotals.total,
        forceCartRoute:
          forceNavigation && !shouldPreserveIdle && currentCart.length > 0,
      })

      if (shouldShowQuoteDetail) {
        if (currentQuoteHeader && Object.keys(currentQuoteHeader).length > 0) {
          postMessageToCustomerDisplay({
            isDetailQuote: true,
            quoteData: currentQuoteHeader,
            isLoadingDetailQuote: false,
            showDiscount: currentQuoteHeader.ShowDiscountOption,
          })
        }
        return
      }

      if (!forceNavigation || shouldPreserveIdle) return

      const productView = customerDisplayProductViewStateRef.current

      const shouldRestoreSearch =
        productView.isSearchView &&
        (currentCart.length === 0 || productView.pendingSearchNavigation)

      if (shouldRestoreSearch) {
        customerDisplayProductViewStateRef.current = {
          ...productView,
          pendingSearchNavigation: false,
        }
        postMessageToCustomerDisplay({
          isSearchProducts: true,
          isLoading: productView.isLoading,
          listProducts: productView.listProducts,
        })

        if (!productView.isProductDetailOpen) return
      }

      if (productView.isProductDetailOpen && productView.productDetail) {
        postMessageToCustomerDisplay({
          isProductDetail: true,
          isSearchProducts: false,
          productDetail: productView.productDetail,
        })
      }
    },
    [postMessageToCustomerDisplay]
  )

  const requestCustomerDisplayOwnershipHandoff = useCallback(
    (currentOwnerTabId, connectionIsConfirmed) => {
      const pendingRequest = pendingOwnershipRequestRef.current
      const now = Date.now()

      if (
        pendingRequest?.ownerTabId === currentOwnerTabId &&
        now - pendingRequest.requestedAt <
          CUSTOMER_DISPLAY_OWNERSHIP_HANDOFF_RETRY_MS
      ) {
        if (connectionIsConfirmed) {
          setIsCustomerDisplaySwitchActive(true)
        }
        return false
      }

      const requestId = createOwnershipRequestId()
      pendingOwnershipRequestRef.current = {
        ownerTabId: currentOwnerTabId,
        requestId,
        requestedAt: now,
      }

      if (connectionIsConfirmed) {
        setIsCustomerDisplaySwitchActive(true)
      }

      postMessageToCustomerDisplay({
        customerDisplayOwnershipRequest: true,
        requestId,
        targetTabId: currentOwnerTabId,
      })

      scheduleOwnershipHandoffTimeout(() => {
        if (pendingOwnershipRequestRef.current?.requestId !== requestId) return

        pendingOwnershipRequestRef.current = null
        const currentTabIsInteractive = isMainAppTabFocused()

        if (!currentTabIsInteractive) {
          setIsCustomerDisplaySwitchActive(false)
        }
        claimOwnershipIfInteractiveRef.current?.()
      }, CUSTOMER_DISPLAY_OWNERSHIP_HANDOFF_RETRY_MS)

      return true
    },
    [postMessageToCustomerDisplay, scheduleOwnershipHandoffTimeout]
  )

  const getCustomerDisplayOwnershipClaim = useCallback(
    ({ allowClaimClosingOwner = false } = {}) => {
      const ownerTabId = customerDisplayTabIdRef.current
      const currentOwnerTabId = getCustomerDisplayOwnerTabId()

      const ownerStatus = getCustomerDisplayOwnerTabStatus()
      const ownerStatusAge = ownerStatus?.updatedAt
        ? Date.now() - ownerStatus.updatedAt
        : Number.POSITIVE_INFINITY
      const preserveConnection = hasConfirmedCustomerDisplayConnection(
        ownerStatus,
        currentOwnerTabId
      )

      if (!currentOwnerTabId || currentOwnerTabId === ownerTabId) {
        return {
          canClaim: true,
          currentOwnerTabId,
          preserveConnection,
          requiresHandoff: false,
        }
      }

      const ownerStatusMatches = ownerStatus?.tabId === currentOwnerTabId
      const ownerStatusIsStale =
        !ownerStatusMatches || ownerStatusAge > CUSTOMER_DISPLAY_OWNER_STALE_MS
      const ownerIsClosing = Boolean(ownerStatus?.isClosing)
      const ownerIsUnavailable =
        ownerStatusIsStale || (allowClaimClosingOwner && ownerIsClosing)

      return {
        canClaim: ownerIsUnavailable,
        currentOwnerTabId,
        preserveConnection,
        requiresHandoff: !ownerIsUnavailable && !ownerIsClosing,
      }
    },
    []
  )

  const announceCustomerDisplayOwnership = useCallback(
    ({ markAsConnected = false } = {}) => {
      const ownerTabId = customerDisplayTabIdRef.current
      const connectionIsConfirmed =
        markAsConnected || isCustomerDisplayWindowConnectedRef.current

      clearOwnershipHandoffTimeout()
      clearOwnerClosingFallbackTimeout()
      pendingOwnershipRequestRef.current = null
      isCustomerDisplayOwnershipConfirmedRef.current = false
      setCustomerDisplayOwnerTabId(ownerTabId, {
        isHidden: isMainAppTabHiddenRef.current,
        isConnected: connectionIsConfirmed,
      })
      setIsCurrentTabCustomerDisplayOwner(true)
      customerDisplayEnabledRef.current = true
      clearCustomerDisplayCloseTimeout()
      dispatch(setEnabledCustomerDisplay(true))

      if (markAsConnected) {
        isActiveCustomerDisplayRef.current = true
        isCustomerDisplayWindowConnectedRef.current = true
        setIsCustomerDisplayWindowConnected(true)
        dispatch(setActiveCustomerDisplay(true))
      } else if (!isCustomerDisplayWindowConnectedRef.current) {
        isActiveCustomerDisplayRef.current = false
        isCustomerDisplayWindowConnectedRef.current = false
        setIsCustomerDisplayWindowConnected(false)
        dispatch(setActiveCustomerDisplay(false))
      }

      setIsCustomerDisplaySwitchActive(connectionIsConfirmed)

      postMessageToCustomerDisplay({
        customerDisplayActivated: true,
        ownerTabId,
      })

      if (isCustomerDisplayRouteSupported(locationPathnameRef.current)) {
        postMessageToCustomerDisplay({ isSleepMode: false })
      }

      sendCompanyData()
      notifyMainRouteSupport()
      syncCustomerDisplayState({ forceNavigation: true })
      sendCustomerDisplayHeartbeat()

      return true
    },
    [
      clearCustomerDisplayCloseTimeout,
      clearOwnerClosingFallbackTimeout,
      clearOwnershipHandoffTimeout,
      dispatch,
      notifyMainRouteSupport,
      postMessageToCustomerDisplay,
      sendCompanyData,
      sendCustomerDisplayHeartbeat,
      syncCustomerDisplayState,
    ]
  )

  const grantCustomerDisplayOwnershipHandoff = useCallback(
    data => {
      const ownerTabId = customerDisplayTabIdRef.current
      const requesterTabId = data.tabId

      if (
        !data.requestId ||
        !requesterTabId ||
        requesterTabId === ownerTabId ||
        getCustomerDisplayOwnerTabId() !== ownerTabId ||
        isMainAppTabVisible()
      ) {
        return false
      }

      const ownerStatus = getCustomerDisplayOwnerTabStatus()
      const connectionIsConfirmed = hasConfirmedCustomerDisplayConnection(
        ownerStatus,
        ownerTabId
      )

      postMessageToCustomerDisplay({
        customerDisplayOwnershipGranted: true,
        connectionIsConfirmed,
        previousOwnerTabId: ownerTabId,
        requestId: data.requestId,
        targetTabId: requesterTabId,
      })

      return true
    },
    [postMessageToCustomerDisplay]
  )

  const acceptCustomerDisplayOwnershipHandoff = useCallback(
    data => {
      const pendingRequest = pendingOwnershipRequestRef.current

      if (
        !pendingRequest ||
        pendingRequest.requestId !== data.requestId ||
        pendingRequest.ownerTabId !== data.previousOwnerTabId ||
        getCustomerDisplayOwnerTabId() !== data.previousOwnerTabId ||
        !isMainAppTabVisible()
      ) {
        return false
      }

      pendingOwnershipRequestRef.current = null

      return announceCustomerDisplayOwnership({
        markAsConnected: Boolean(data.connectionIsConfirmed),
      })
    },
    [announceCustomerDisplayOwnership]
  )

  const respondToCustomerDisplayOwnershipRecovery = useCallback(
    data => {
      const previousOwnerTabId = data.previousOwnerTabId

      if (
        !isCustomerDisplaySourceMessage(data) ||
        !data.requestId ||
        !previousOwnerTabId ||
        getCustomerDisplayOwnerTabId() !== previousOwnerTabId ||
        previousOwnerTabId === customerDisplayTabIdRef.current ||
        !isMainAppTabVisible()
      ) {
        return false
      }

      pendingOwnershipRequestRef.current = {
        ownerTabId: previousOwnerTabId,
        requestId: data.requestId,
        requestedAt: Date.now(),
      }

      postMessageToCustomerDisplay({
        customerDisplayOwnershipRecoveryCandidate: true,
        previousOwnerTabId,
        requestId: data.requestId,
      })

      return true
    },
    [postMessageToCustomerDisplay]
  )

  const activateCustomerDisplay = useCallback(async () => {
    const hasSecondaryScreen = hasCustomerDisplaySecondaryScreen()
    const windowConfig = await prepareCustomerDisplayWindowConfig()

    if (hasSecondaryScreen && !windowConfig) {
      setCustomerDisplayActivationError(
        'CUSTOMER_DISPLAY.WINDOW_MANAGEMENT_PERMISSION_REQUIRED'
      )
      return false
    }

    setCustomerDisplayActivationError('')
    const preparedWindow = openCustomerDisplayWindow({
      url: '',
      skipPresenceCheck: true,
      windowConfig,
    })

    if (!preparedWindow) return false

    const preparedWindowIsCustomerDisplay =
      isCustomerDisplayWindowHandle(preparedWindow)
    const existingWindowIsActive =
      preparedWindowIsCustomerDisplay ||
      (await hasActiveCustomerDisplayWindowPresence())

    if (existingWindowIsActive) {
      if (!preparedWindowIsCustomerDisplay) {
        closeCustomerDisplayWindowHandle(preparedWindow)
      } else {
        activeCustomerDisplayWindowRef.current = preparedWindow
      }

      customerDisplayEnabledRef.current = true
      dispatch(setEnabledCustomerDisplay(true))
      setIsCustomerDisplaySwitchActive(true)
      claimOwnershipIfInteractiveRef.current?.()
      return true
    }

    if (
      !navigateCustomerDisplayWindow(preparedWindow, {
        skipPresenceCheck: true,
        windowConfig,
      })
    ) {
      closeCustomerDisplayWindowHandle(preparedWindow)
      return false
    }

    activeCustomerDisplayWindowRef.current = preparedWindow
    clearOwnershipHandoffTimeout()
    pendingOwnershipRequestRef.current = null
    lastCustomerDisplayHeartbeatAckRef.current = Date.now()
    customerDisplayHeartbeatMonitorStartedAtRef.current = Date.now()
    setLoaderFullScreen(true)
    announceCustomerDisplayOwnership({ markAsConnected: true })

    return true
  }, [announceCustomerDisplayOwnership, clearOwnershipHandoffTimeout, dispatch])

  const claimCustomerDisplayOwnership = useCallback(
    ({ allowClaimClosingOwner = false } = {}) => {
      const hasKnownCustomerDisplay =
        customerDisplayEnabledRef.current || getCustomerDisplayOwnerTabId()

      if (!hasKnownCustomerDisplay) return false
      const {
        canClaim,
        currentOwnerTabId,
        preserveConnection,
        requiresHandoff,
      } = getCustomerDisplayOwnershipClaim({ allowClaimClosingOwner })

      if (requiresHandoff) {
        requestCustomerDisplayOwnershipHandoff(
          currentOwnerTabId,
          preserveConnection
        )
        return false
      }

      if (!canClaim) return false

      return announceCustomerDisplayOwnership({
        markAsConnected: preserveConnection,
      })
    },
    [
      announceCustomerDisplayOwnership,
      getCustomerDisplayOwnershipClaim,
      requestCustomerDisplayOwnershipHandoff,
    ]
  )

  const claimCustomerDisplayOwnershipIfInteractive = useCallback(
    ({ allowWithoutFocus = false, allowClaimClosingOwner = false } = {}) => {
      if (!isMainAppTabVisible()) return false
      if (!allowWithoutFocus && !isMainAppTabFocused()) {
        return false
      }

      const now = Date.now()
      if (
        now - lastOwnershipClaimAtRef.current <
        CUSTOMER_DISPLAY_OWNERSHIP_CLAIM_DEBOUNCE_MS
      ) {
        return false
      }

      const ownershipClaimed = claimCustomerDisplayOwnership({
        allowClaimClosingOwner,
      })

      if (ownershipClaimed) {
        lastOwnershipClaimAtRef.current = now
      }

      return ownershipClaimed
    },
    [claimCustomerDisplayOwnership]
  )

  claimOwnershipIfInteractiveRef.current = () =>
    claimCustomerDisplayOwnershipIfInteractive({ allowWithoutFocus: true })

  const deactivateCustomerDisplay = useCallback(() => {
    const ownerTabId = customerDisplayTabIdRef.current

    if (getCustomerDisplayOwnerTabId() !== ownerTabId) {
      setIsCurrentTabCustomerDisplayOwner(false)
      markCustomerDisplayAsDisconnected()
      return false
    }

    clearCustomerDisplayCloseTimeout()
    clearOwnershipHandoffTimeout()
    pendingOwnershipRequestRef.current = null
    lastCustomerDisplayHeartbeatAckRef.current = null
    customerDisplayHeartbeatMonitorStartedAtRef.current = null
    customerDisplayEnabledRef.current = false
    isActiveCustomerDisplayRef.current = false
    isCustomerDisplayWindowConnectedRef.current = false
    isCustomerDisplayOwnershipConfirmedRef.current = false
    clearCustomerDisplayOwnerTabId(ownerTabId)
    setIsCurrentTabCustomerDisplayOwner(false)
    setIsCustomerDisplayWindowConnected(false)
    setIsCustomerDisplaySwitchActive(false)
    setLoaderFullScreen(false)
    postMessageToCustomerDisplay({ windowClose: true })
    closeCustomerDisplayWindow()
    dispatch(setEnabledCustomerDisplay(false))
    dispatch(setActiveCustomerDisplay(false))

    return true
  }, [
    clearCustomerDisplayCloseTimeout,
    clearOwnershipHandoffTimeout,
    closeCustomerDisplayWindow,
    dispatch,
    markCustomerDisplayAsDisconnected,
    postMessageToCustomerDisplay,
  ])

  const toggleCustomerDisplay = useCallback(() => {
    if (isCustomerDisplaySwitchActive) {
      deactivateCustomerDisplay()
      return
    }

    activateCustomerDisplay()
  }, [
    activateCustomerDisplay,
    deactivateCustomerDisplay,
    isCustomerDisplaySwitchActive,
  ])

  useEffect(() => {
    if (!isActiveCustomerDisplay || !isCurrentTabCustomerDisplayOwner) return

    notifyMainRouteSupport()
  }, [
    location.pathname,
    isActiveCustomerDisplay,
    isCurrentTabCustomerDisplayOwner,
    notifyMainRouteSupport,
  ])

  useEffect(() => {
    if (!isActiveCustomerDisplay || !isCurrentTabCustomerDisplayOwner) return

    syncCustomerDisplayState()
  }, [
    cart,
    isActiveCustomerDisplay,
    isCurrentTabCustomerDisplayOwner,
    quoteHeader,
    syncCustomerDisplayState,
  ])

  useEffect(() => {
    if (!shouldMonitorCustomerDisplayConnection) return undefined

    customerDisplayHeartbeatMonitorStartedAtRef.current = Date.now()
    sendCustomerDisplayHeartbeat()
    const heartbeatInterval = setInterval(
      sendCustomerDisplayHeartbeat,
      CUSTOMER_DISPLAY_HEARTBEAT_INTERVAL_MS
    )

    return () => {
      clearInterval(heartbeatInterval)
    }
  }, [sendCustomerDisplayHeartbeat, shouldMonitorCustomerDisplayConnection])

  useEffect(() => {
    if (!customerDisplayEnabled) {
      markCustomerDisplayAsDisconnected()
      return undefined
    }

    if (!shouldMonitorCustomerDisplayConnection) return undefined

    if (!customerDisplayHeartbeatMonitorStartedAtRef.current) {
      customerDisplayHeartbeatMonitorStartedAtRef.current = Date.now()
    }

    const validateCustomerDisplayConnection = () => {
      if (isMainAppTabHiddenRef.current) return

      const lastHeartbeatAckAt = lastCustomerDisplayHeartbeatAckRef.current
      const heartbeatReferenceAt =
        lastHeartbeatAckAt ||
        customerDisplayHeartbeatMonitorStartedAtRef.current

      if (!heartbeatReferenceAt) return

      const heartbeatAge = Date.now() - heartbeatReferenceAt
      if (heartbeatAge > CUSTOMER_DISPLAY_HEARTBEAT_ACK_TIMEOUT_MS) {
        markCustomerDisplayAsDisconnected()
      }
    }

    const heartbeatAckInterval = setInterval(
      validateCustomerDisplayConnection,
      CUSTOMER_DISPLAY_HEARTBEAT_ACK_CHECK_INTERVAL_MS
    )

    return () => {
      clearInterval(heartbeatAckInterval)
    }
  }, [
    customerDisplayEnabled,
    markCustomerDisplayAsDisconnected,
    shouldMonitorCustomerDisplayConnection,
  ])

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isHidden = document.hidden
      isMainAppTabHiddenRef.current = isHidden

      if (!customerDisplayEnabledRef.current) return

      if (isHidden) {
        preserveIdleOnCreateQuoteEntryRef.current = false
      }

      refreshCustomerDisplayOwnerStatus({ isHidden })
      postMessageToCustomerDisplay({ mainAppTabHidden: isHidden })

      if (!isHidden) {
        const now = Date.now()
        lastCustomerDisplayHeartbeatAckRef.current = now
        customerDisplayHeartbeatMonitorStartedAtRef.current = now
        sendCustomerDisplayHeartbeat()
      }
    }

    const notifyMainAppTabClosing = event => {
      if (event?.persisted) return
      if (mainAppTabCloseNotifiedRef.current) return

      mainAppTabCloseNotifiedRef.current = true

      isMainAppTabHiddenRef.current = false
      refreshCustomerDisplayOwnerStatus({
        isHidden: false,
        isClosing: true,
      })

      notifyCustomerDisplayMainTabClosing(customerDisplayTabIdRef.current)
      postMessageToCustomerDisplay({
        mainAppTabClosing: true,
        mainAppTabHidden: false,
      })
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', notifyMainAppTabClosing)
    window.addEventListener('beforeunload', notifyMainAppTabClosing)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', notifyMainAppTabClosing)
      window.removeEventListener('beforeunload', notifyMainAppTabClosing)
    }
  }, [
    postMessageToCustomerDisplay,
    refreshCustomerDisplayOwnerStatus,
    sendCustomerDisplayHeartbeat,
  ])

  useLayoutEffect(() => {
    claimCustomerDisplayOwnershipIfInteractive()
    const claimOwnershipAfterActivation = () =>
      claimCustomerDisplayOwnershipIfInteractive({ allowWithoutFocus: true })

    window.addEventListener('focus', claimOwnershipAfterActivation)
    document.addEventListener('visibilitychange', claimOwnershipAfterActivation)

    return () => {
      window.removeEventListener('focus', claimOwnershipAfterActivation)
      document.removeEventListener(
        'visibilitychange',
        claimOwnershipAfterActivation
      )
    }
  }, [claimCustomerDisplayOwnershipIfInteractive])

  useEffect(() => {
    const syncOwnershipFromStorage = event => {
      if (event.key === CUSTOMER_DISPLAY_OWNER_TAB_ID_STORAGE_KEY) {
        if (!event.newValue) {
          clearOwnershipHandoffTimeout()
          pendingOwnershipRequestRef.current = null
          setIsCurrentTabCustomerDisplayOwner(false)
          markCustomerDisplayAsDisconnected()
        } else if (event.newValue !== customerDisplayTabIdRef.current) {
          clearOwnershipHandoffTimeout()
          pendingOwnershipRequestRef.current = null
          setIsCurrentTabCustomerDisplayOwner(false)
          markCustomerDisplayAsDisconnected({
            preserveSwitchState: !isMainAppTabVisible(),
          })
        } else {
          setIsCurrentTabCustomerDisplayOwner(true)
        }

        return
      }

      if (event.key !== CUSTOMER_DISPLAY_OWNER_TAB_STATUS_STORAGE_KEY) return

      const ownerTabId = getCustomerDisplayOwnerTabId()
      const ownerStatus = getCustomerDisplayOwnerTabStatus()
      const currentTabIsOwner = ownerTabId === customerDisplayTabIdRef.current
      const currentTabIsInteractive = isMainAppTabFocused()

      if (currentTabIsOwner) {
        setIsCustomerDisplaySwitchActive(
          hasConfirmedCustomerDisplayConnectionForTab(
            ownerStatus,
            ownerTabId,
            customerDisplayTabIdRef.current
          )
        )
        return
      }

      if (ownerStatus?.isClosing) {
        setIsCustomerDisplaySwitchActive(
          hasConfirmedCustomerDisplayConnection(ownerStatus, ownerTabId)
        )
        scheduleOwnerClosingFallbackTimeout(() => {
          const currentOwner = getCustomerDisplayOwnerTabId()
          const currentStatus = getCustomerDisplayOwnerTabStatus()
          if (
            currentOwner === ownerTabId &&
            currentStatus?.isClosing &&
            isMainAppTabFocused()
          ) {
            claimCustomerDisplayOwnershipIfInteractive({
              allowWithoutFocus: true,
              allowClaimClosingOwner: true,
            })
          }
        }, CUSTOMER_DISPLAY_CLOSING_FALLBACK_TIMEOUT_MS)
        return
      }

      clearOwnerClosingFallbackTimeout()

      if (!currentTabIsInteractive) return

      setIsCustomerDisplaySwitchActive(
        hasConfirmedCustomerDisplayConnection(ownerStatus, ownerTabId)
      )
      claimCustomerDisplayOwnershipIfInteractive()
    }

    window.addEventListener('storage', syncOwnershipFromStorage)

    return () => {
      window.removeEventListener('storage', syncOwnershipFromStorage)
    }
  }, [
    claimCustomerDisplayOwnershipIfInteractive,
    clearOwnerClosingFallbackTimeout,
    clearOwnershipHandoffTimeout,
    markCustomerDisplayAsDisconnected,
    scheduleOwnerClosingFallbackTimeout,
  ])

  useEffect(() => {
    if (!isActiveCustomerDisplay || !isCurrentTabCustomerDisplayOwner) return

    sendCompanyData()
  }, [
    CompanyId,
    BranchOfficeId,
    isActiveCustomerDisplay,
    isCurrentTabCustomerDisplayOwner,
    sendCompanyData,
  ])

  useEffect(() => {
    if (!isActiveCustomerDisplay || !isCurrentTabCustomerDisplayOwner) {
      return undefined
    }

    let disposeWatcher
    let isMounted = true

    watchSecondaryScreenDisconnect(() => {
      if (isMounted) {
        deactivateCustomerDisplay()
      }
    }).then(dispose => {
      if (isMounted) {
        disposeWatcher = dispose
        return
      }

      dispose?.()
    })

    return () => {
      isMounted = false
      disposeWatcher?.()
    }
  }, [
    deactivateCustomerDisplay,
    isActiveCustomerDisplay,
    isCurrentTabCustomerDisplayOwner,
  ])

  useEffect(() => {
    const channel = getChannel()

    if (!channel) return undefined

    const handleMessage = event => {
      const data = event.data || {}

      cacheCustomerDisplayProductViewState(data)

      if (!isCustomerDisplayMessageForCurrentTab(data)) return

      if (data.customerDisplayOwnershipRecoveryRequest) {
        respondToCustomerDisplayOwnershipRecovery(data)
        return
      }

      if (data.customerDisplayOwnershipRequest) {
        grantCustomerDisplayOwnershipHandoff(data)
        return
      }

      if (data.customerDisplayOwnershipGranted) {
        acceptCustomerDisplayOwnershipHandoff(data)
        return
      }

      if (data.customerDisplayHeartbeatAck) {
        if (!customerDisplayEnabledRef.current) return

        if (!confirmCustomerDisplayConnection()) return

        if (
          customerDisplayProductViewStateRef.current.pendingSearchNavigation
        ) {
          syncCustomerDisplayState({ forceNavigation: true })
        }

        if (!isActiveCustomerDisplayRef.current) {
          isActiveCustomerDisplayRef.current = true
          dispatch(setActiveCustomerDisplay(true))
        }

        return
      }

      if (data.requestCustomerDisplaySync) {
        syncCustomerDisplayState({ forceNavigation: true })
      }

      if (data.customerDisplayWindowClosed) {
        scheduleCustomerDisplayCloseTimeout(
          markCustomerDisplayAsClosed,
          CUSTOMER_DISPLAY_WINDOW_CLOSE_GRACE_PERIOD_MS
        )
        return
      }

      if (data.customerDisplayReady === true || data.isFullScreen === true) {
        if (!customerDisplayEnabledRef.current) return

        clearCustomerDisplayCloseTimeout()
        if (!confirmCustomerDisplayConnection()) return
        setLoaderFullScreen(false)
        sendCompanyData()
        notifyMainRouteSupport()
      }
    }

    channel.addEventListener('message', handleMessage)

    return () => {
      channel.removeEventListener('message', handleMessage)
    }
  }, [
    acceptCustomerDisplayOwnershipHandoff,
    getChannel,
    cacheCustomerDisplayProductViewState,
    clearCustomerDisplayCloseTimeout,
    confirmCustomerDisplayConnection,
    grantCustomerDisplayOwnershipHandoff,
    markCustomerDisplayAsClosed,
    notifyMainRouteSupport,
    respondToCustomerDisplayOwnershipRecovery,
    scheduleCustomerDisplayCloseTimeout,
    sendCompanyData,
    syncCustomerDisplayState,
    dispatch,
  ])

  useEffect(() => {
    return () => {
      if (channelRef.current) {
        channelRef.current.close()
        channelRef.current = null
      }
    }
  }, [])

  const postToCustomerDisplay = useCallback(
    message => {
      postMessageToCustomerDisplay(message)
    },
    [postMessageToCustomerDisplay]
  )

  const clearCustomerDisplayActivationError = useCallback(() => {
    setCustomerDisplayActivationError('')
  }, [])

  return {
    clearCustomerDisplayActivationError,
    customerDisplayActivationError,
    isCustomerDisplaySwitchActive,
    loaderFullScreen,
    toggleCustomerDisplay,
    postToCustomerDisplay,
  }
}
